//#region IMPORT
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import M_WorkPlans_BASIC from './M_WorkPlans_BASIC';
import Global from '../../../../Services/Common/Global';
import { useDispatch, useSelector } from 'react-redux';
import { CommonActions } from '../../../../Reducer/CommonReducer';
import { BottomSheetActions } from '../../../../Reducer/BottomSheetReducer';
import { TbmActions } from '../../../../Reducer/TbmReducer';
import { useBackStack } from '../../../../Hooks/back/useBackStack';
import { useTbmSessionSave } from '../hook/useSaveTbmSession';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import { usePdfStepTransition } from '../hook/usePdfStepTransition';
import { shouldOpenMeetingDrawerOnNext } from '../hook/tbmStepFlow';
import { buildDocumentFileUrl, getStepContents } from '../hook/tbmContents';
//#endregion

function M_WorkPlans_BASICF(props) {
  //#region 변수
  const dispatch = useDispatch();
  const {
    sessionData,
    stepData,
    participantData,
    pageNumber,
    contentData,
    contentIndex,
  } = useSelector((state) => state.tbm);
  const { userInfo } = useSelector((state) => state.BaseInfo);
  const activeStep = useSelector((state) => state.common.activeStep);
  const isHost = participantData.hostYn === 'Y';

  const [loading, setLoading] = useState(false);
  const [transitionDirection, setTransitionDirection] = useState('next');
  const [fullUrl, setFullUrl] = useState();
  const [renderPreview, setRenderPreview] = useState(null);
  const [numPages, setNumPages] = useState();
  const [pageWidth, setPageWidth] = useState(window.innerWidth * 1);
  const [pageViewHeight, setPageViewHeight] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);

  const isMountedRef = React.useRef(true);

  const stepContents = useMemo(
    () => getStepContents(contentData, props.stepCode),
    [contentData, props.stepCode]
  );

  const currentFile = useMemo(
    () => stepContents[contentIndex],
    [stepContents, contentIndex]
  );
  //#endregion

  const CleanupForTransition = useCallback(() => {
    setRenderPreview(null);
    setFullUrl(undefined);
    setNumPages(undefined);
    setLoading(false);
  }, []);

  const { isTransitioning, startTransition } = usePdfStepTransition({
    cleanup: CleanupForTransition,
    isMountedRef,
  });

  //#region 커스텀훅
  useBackStack({
    type: 'custom',
    enabled: activeStep > 0,
    onBack: () => {
      if (!isHost) {
        dispatch(
          showSnackbar({
            message: 'TBM 진행중 뒤로가기가 불가능합니다.',
            type: 'warning',
          })
        );
        return;
      }
      setTransitionDirection('prev');
      props.ActiveButton({ type: 'stepChange', step: activeStep - 1 });
      dispatch(
        TbmActions.setStepTransition({
          isTransitioning: true,
          direction: 'prev',
        })
      );
      startTransition(() => {
        dispatch(
          TbmActions.setStepTransition({
            isTransitioning: false,
            direction: 'prev',
          })
        );
        dispatch(CommonActions.setActiveStep(activeStep - 1));
      });
    },
  });
  const { SaveTbmSession } = useTbmSessionSave();
  //#endregion

  //#region 이펙트
  const CleanupPreview = () => {
    setRenderPreview(null);
  };

  const LoadFile = async (path) => {
    CleanupPreview();

    await new Promise((r) =>
      requestAnimationFrame(() => requestAnimationFrame(r))
    );

    if (!isMountedRef.current) return;

    if (!path) {
      setFullUrl(undefined);
      setRenderPreview(null);
      return;
    }

    const fileUrl = buildDocumentFileUrl(path, Global.file.useUrl);
    setFullUrl(fileUrl);
    setRenderPreview(fileUrl);
  };

  const HandleDocumentError = () => {
    dispatch(
      showSnackbar({
        message:
          '파일을 불러올 수 없습니다. 파일이 없거나 손상되었을 수 있습니다.',
        type: 'error',
      })
    );
  };

  // 컨텐츠 탭 초기값 설정
  useEffect(() => {
    if (!stepContents.length) return;

    const curContentOrder = stepData.find(
      (f) => f.stepCode === props.stepCode
    )?.curContentOrder;

    const index = stepContents.findIndex(
      (c) => Number(c.contentOrder) === Number(curContentOrder)
    );
    dispatch(TbmActions.setContentIndex(index >= 0 ? index : 0));
  }, [
    sessionData.sessionCd,
    props.stepCode,
    stepContents.length,
    stepData.length,
  ]);

  useEffect(() => {
    isMountedRef.current = true;
    setIsInitialized(false);
    LoadFile(currentFile?.filePath);
    setNumPages(undefined);
    dispatch(TbmActions.setPageNumber(currentFile?.lastPosition ?? '1'));

    const handleResize = () => {
      setPageWidth(window.innerWidth * 1);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      isMountedRef.current = false;
      window.removeEventListener('resize', handleResize);
      CleanupPreview();
    };
  }, [currentFile?.filePath]);

  // 페이지 번호 변경 시 세션 저장
  useEffect(() => {
    const lastPosition = currentFile?.lastPosition;
    if (!isInitialized) {
      if (
        pageNumber == lastPosition ||
        (lastPosition == 0 && pageNumber == 1)
      ) {
        setIsInitialized(true);
      } else {
        return;
      }
    }
    if (loading) setLoading(false);
    if (Object.keys(sessionData).length === 0) return;
    if (participantData.hostYn === 'Y') {
      SaveTbmSession([
        {
          ...sessionData,
          curStepOrder: stepData.find((f) => f.stepCode === props.stepCode)
            ?.stepOrder,
          curStepCode: stepData.find((f) => f.stepCode === props.stepCode)
            ?.stepCode,
          curPosition: lastPosition == 0 ? 1 : pageNumber,
          curContentOrder: currentFile?.contentOrder,
        },
      ]);
    }
  }, [pageNumber]);

  useEffect(() => {
    if (!numPages || numPages < 1) return;
    const safe = Math.min(Math.max(1, pageNumber), numPages);
    if (safe === pageNumber) return;
    dispatch(TbmActions.setPageNumber(safe));
    if (isHost) {
      props.ActiveButton({
        type: 'pageChange',
        pageNumber: safe,
      });
    }
  }, [numPages, pageNumber]);
  //#endregion

  //#region 나머지 함수
  const getExtensionFromUrl = (url = '') => {
    const parts = url?.split('.');
    return parts?.length > 1 ? parts.pop().toLowerCase() : '';
  };
  const onDocumentLoadSuccess = ({ numPages: total }) => setNumPages(total);

  const HandleContentTabChange = (index) => {
    if (index === contentIndex) return;
    const target = stepContents[index];
    const lastPosition = target?.lastPosition ?? '1';

    dispatch(TbmActions.setContentIndex(index));
    dispatch(TbmActions.setPageNumber(lastPosition));

    if (isHost) {
      props.ActiveButton({
        type: 'contentIndexChange',
        contentIndex: index,
      });
    }

    SaveTbmSession([
      {
        ...sessionData,
        curContentOrder: target?.contentOrder,
        curPosition: lastPosition,
      },
    ]);
    setIsInitialized(false);
    setNumPages(undefined);
  };

  const goToPrevPage = () => {
    if (pageNumber > 1) {
      setLoading(true);
      const prev = pageNumber - 1;
      dispatch(TbmActions.setPageNumber(prev));
      props.ActiveButton({
        type: 'pageChange',
        pageNumber: prev,
      });
    } else {
      setTransitionDirection('prev');
      dispatch(
        TbmActions.setStepTransition({
          isTransitioning: true,
          direction: 'prev',
        })
      );
      startTransition(() => {
        dispatch(
          TbmActions.setStepTransition({
            isTransitioning: false,
            direction: 'prev',
          })
        );
        dispatch(CommonActions.setActiveStep(Math.max(0, activeStep - 1)));
      });
    }
  };

  const goToNextPage = () => {
    if (pageNumber < numPages) {
      setLoading(true);
      const next = pageNumber + 1;
      dispatch(TbmActions.setPageNumber(next));
      props.ActiveButton({
        type: 'pageChange',
        pageNumber: next,
      });
    }
  };

  const advanceToNextStep = useCallback(() => {
    if (shouldOpenMeetingDrawerOnNext(stepData, props.stepCode)) {
      // 웹소켓 왕복(echo)에 의존하지 않고 즉시 로컬에서 팝업을 띄움
      dispatch(BottomSheetActions.OpenBottomSheet({ type: 'meetingDrawer' }));
      props.ActiveButton({ type: 'openMeetingDrawer' });
      return;
    }
    setTransitionDirection('next');
    props.ActiveButton({ type: 'stepChange', step: activeStep + 1 });
    dispatch(
      TbmActions.setStepTransition({ isTransitioning: true, direction: 'next' })
    );
    startTransition(() => {
      dispatch(
        TbmActions.setStepTransition({
          isTransitioning: false,
          direction: 'next',
        })
      );
      dispatch(CommonActions.setActiveStep(activeStep + 1));
    });
  }, [
    stepData,
    props.stepCode,
    startTransition,
    props.ActiveButton,
    activeStep,
  ]);
  //#endregion

  return (
    <M_WorkPlans_BASIC
      userInfo={userInfo}
      loading={loading}
      setLoading={setLoading}
      onDocumentLoadSuccess={onDocumentLoadSuccess}
      pageWidth={pageWidth}
      numPages={numPages}
      pageViewHeight={pageViewHeight}
      setPageViewHeight={setPageViewHeight}
      renderPreview={renderPreview}
      goToPrevPage={goToPrevPage}
      goToNextPage={goToNextPage}
      ActiveButton={props.ActiveButton}
      fullUrl={fullUrl}
      getExtensionFromUrl={getExtensionFromUrl}
      onDocumentError={HandleDocumentError}
      isTransitioning={isTransitioning}
      transitionDirection={transitionDirection}
      onAdvanceToNextStep={advanceToNextStep}
      contentFiles={stepContents}
      contentIndex={contentIndex}
      onContentIndexChange={HandleContentTabChange}
      showContentTabs={isHost}
    />
  );
}

export default M_WorkPlans_BASICF;
